/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/** Connected account, Compose, and the folder list with unread counts. */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { providerForAccount } from 'hooks/useEmailProviderConnect';
import { getFolderIcon } from '../folderIcons';

export function AccountBadge({ account, onOpenSettings }) {
  const provider = providerForAccount(account?.email_provider);
  const address = account?.email_address || '';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, minWidth: 0 }}>
      {provider && <Box component="img" src={provider.logo} alt={provider.label} sx={{ width: 24, height: 24, flexShrink: 0 }} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.2 }}>
          {provider ? provider.label : account?.email_provider}
        </Typography>
        <Tooltip title={address} placement="bottom-start" disableHoverListener={!address}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {address}
          </Typography>
        </Tooltip>
      </Box>
      <Tooltip title="Email settings">
        <IconButton size="small" onClick={onOpenSettings} aria-label="Email settings">
          <SettingsOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

AccountBadge.propTypes = {
  account: PropTypes.shape({ email_provider: PropTypes.string, email_address: PropTypes.string }),
  onOpenSettings: PropTypes.func.isRequired,
};

AccountBadge.defaultProps = { account: null };

function InboxSidebar({ account, folders, foldersLoading, currentMailbox, onSelectFolder, onCompose, onOpenSettings }) {
  return (
    <Box
      component="nav"
      aria-label="Mail folders"
      sx={{ width: 232, flexShrink: 0, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <AccountBadge account={account} onOpenSettings={onOpenSettings} />
      <Box sx={{ px: 2, pb: 1.5 }}>
        <Button fullWidth variant="contained" startIcon={<EditOutlinedIcon />} onClick={onCompose} sx={{ py: 1 }}>
          Compose
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1 }}>
        {foldersLoading && !folders.length ? (
          Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={32} sx={{ mx: 1, my: 0.75 }} />
          ))
        ) : (
          <List dense disablePadding>
            {folders.map((folder) => {
              const FolderIcon = getFolderIcon(folder, account?.email_provider);
              const selected = currentMailbox === folder.param;
              return (
                <ListItemButton
                  key={folder.key}
                  selected={selected}
                  onClick={() => onSelectFolder(folder)}
                  sx={{ pl: 1.5 + Math.min(folder.depth, 3) * 1.5, py: 0.75, mb: 0.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: selected ? 'primary.main' : 'text.secondary' }}>
                    <FolderIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={folder.label}
                    primaryTypographyProps={{
                      noWrap: true,
                      variant: 'body2',
                      fontWeight: selected || folder.unread ? 600 : 400,
                    }}
                  />
                  {folder.unread > 0 && (
                    <Typography variant="caption" sx={{ fontWeight: 600, ml: 1, color: selected ? 'inherit' : 'primary.main' }}>
                      {folder.unread > 999 ? '999+' : folder.unread}
                    </Typography>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}

InboxSidebar.propTypes = {
  account: PropTypes.object,
  folders: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      param: PropTypes.string.isRequired,
      kind: PropTypes.string,
      unread: PropTypes.number,
      depth: PropTypes.number,
    })
  ).isRequired,
  foldersLoading: PropTypes.bool,
  currentMailbox: PropTypes.string.isRequired,
  onSelectFolder: PropTypes.func.isRequired,
  onCompose: PropTypes.func.isRequired,
  onOpenSettings: PropTypes.func.isRequired,
};

InboxSidebar.defaultProps = { account: null, foldersLoading: false };

export default InboxSidebar;
